import {useCallback, useContext} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {getLicenseBilling} from 'src/openapi/admin/apis/GetLicenseBillingApi';
import {setLicenseBilling} from 'src/openapi/admin/apis/SetLicenseBillingApi';
import {BillingStatus as BillingStatuses} from 'src/openapi/admin/models/BillingStatus';
import {useAppDispatch} from 'src/store/hooks';

import type {BillingInfo} from 'src/openapi/admin/models/BillingInfo';

type Props = {
    licenseId: number,
};

const DEFAULT_ACCOUNT_BILLING: BillingInfo = {
    status: BillingStatuses.IN_GOOD_STANDING,
    note: '',
};

export const useAccountBilling = ({licenseId}: Props) => {
    const apiDispatch = useAppDispatch();
    const {addError, removeError} = useContext(ValidationContext);

    const editValidation = useCallback((accountBilling: BillingInfo) => {
        if (accountBilling.note) {
            removeError('account-billing-comment');
        }
    }, [removeError]);

    const saveValidation = useCallback((accountBilling: BillingInfo) => {
        const isValid = !!accountBilling.note;

        if (!isValid) {
            addError({
                controlId: 'account-billing-comment',
                code: 'NO_ACCOUNT_BILLING_COMMENT',
                message: 'A comment must be included when changing billing status',
            });
        }

        return isValid;
    }, [addError]);

    const getDispatchAction = useCallback(() => apiDispatch(
        getLicenseBilling({licenseId}),
    ), [licenseId, apiDispatch]);

    const saveDispatchAction = useCallback((billingInfo: BillingInfo) => apiDispatch(
        setLicenseBilling({licenseId, billingInfo}),
    ), [licenseId, apiDispatch]);

    return useCommonDataHook({
        initValue: DEFAULT_ACCOUNT_BILLING,
        getDispatchAction,
        saveDispatchAction,
        editValidation,
        saveValidation,
    });
};
